<script setup lang="ts">
interface Props {
  categoryId?: string;
  id?: string;
  limit?: number; 
}

const { path } = useRoute()
const { homeCategory1 } = useRuntimeConfig().public
const { categoryId, id = null, limit = 12 } = defineProps<Props>()

const getCrossSells = async (categoryId: string, limit: number) => {
  const { elements: products } = await $fetch<{ elements: Array<any> }>(`/store-api/product-listing/${categoryId}`, {
    method: 'POST',
    body: {
      limit,
      filter: [
        {
          type: 'not',
          operator: 'or',
          queries: [
            {
              field: 'product.seo_url',
              type: 'equals',
              value: 'null'
            },
            {
              field: 'product.id',
              type: 'equals',
              value: id
            },
            {
              field: 'product.parentId',
              type: 'equals',
              value: id
            }
          ]
        }
      ],
      associations: {
        productReviews: {}
      },
      includes: {
        product_listing: ['elements'],
        property_group: ['id', 'name', 'options'],
        property_group_option: ['id', 'name'],
        product: ['id', 'name', 'parentId', 'calculatedPrice', 'calculatedPrices', 'cover', 'createdAt', 'ratingAverage', 'productReviews', 'sales', 'seoUrls', 'urlPrefix', 'customFields.product_baseCategory', 'customFields.product_city'],
        product_media: ['media'],
        media: ['url', 'metaData', 'alt']
      }
    },
    query: {
      url: path
    }
  })

  const baseCategories: Set<string> = new Set(products.map(product => product.customFields.product_baseCategory))

  if (baseCategories.size) {
    const { elements: categories } = await $fetch('/store-api/category', {
      method: 'POST',
      body: {
        includes: {
          tandemite_category_stats: ['rootProductCount', 'reviewCount'],
          category: ['id', 'stats']
        },
        filter: [
          {
            type: 'equalsAny',
            field: 'id',
            value: [...baseCategories]
          }
        ],
        associations: {
          stats: {}
        }
      },
      query: {
        url: path
      }
    })

    products.forEach(product => {
      const categoryId = categories.find((item: any) => item.id === product.customFields.product_baseCategory)
      product.customFields.rootProductCount = categoryId?.extensions.stats[0]?.rootProductCount || 0
    })
  }

  return products
}

const { data: crossSells } = await useLazyAsyncData('crossSelling', async () => {
  if (categoryId) {
    const crossSells = await getCrossSells(categoryId, limit)
    if (!!crossSells.length) {
      return crossSells
    }
  }
  
  const crossSells = await getCrossSells(homeCategory1, limit)
  return crossSells
})
</script>

<template>
  <div v-if="!!crossSells?.length">
    <div class="text-24 text-primary font-bold">
      Podobne produkty
    </div>
    <Carousel :options="{ slidesToScroll: 'auto', containScroll: 'keepSnaps', loop: true }" class="relative mt-[15px] [--slide-spacing:5px] sm:[--slide-spacing:15px] lg:[--slide-spacing:30px]">
      <Carousel.Slide v-for="crossSell in crossSells" :key="crossSell.id" class="shrink-0 basis-1/2 sm:basis-1/3 md:basis-1/4">
        <ProductListItem :products="crossSell" class="mb-[30px] group max-w-[262px]" />
      </Carousel.Slide>

      <template #addons="{ prev, next }">
        <button aria-label="Poprzedni slajd" class="z-1 text-16 bg-white/60 absolute w-10 h-10 top-1/2 -translate-y-1/2 cursor-pointer rounded flex items-center justify-center left-2" @click.stop="prev">
          <i class="icon-arrow-button -rotate-180" />
        </button>

        <button aria-label="Następny slajd" class="z-1 text-16 bg-white/60 absolute w-10 h-10 top-1/2 -translate-y-1/2 cursor-pointer rounded flex items-center justify-center right-2" @click.stop="next">
          <i class="icon-arrow-button" />
        </button>
      </template>
    </Carousel>
  </div>
</template>
